import * as React from "react";
import { forwardRef } from "react";
import { Stepper, Step, StepLabel, Typography } from "@material-ui/core";
import { getStatusString, getLocationString } from "../../newOrder/components/helperFunctions/index.js";
import moment from "moment";

const Field = ({ label, value }) => (
    <div style={{ marginBottom: 12 }}>
        <div
            style={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#1a1a1a",
                opacity: 0.4,
                marginBottom: 2,
            }}
        >
            {label}
        </div>
        <div
            style={{
                fontSize: 12,
                color: value ? "#1a1a1a" : "#bbb",
                fontWeight: value ? 500 : 400,
                opacity: value ? 0.85 : 0.35,
            }}
        >
            {value || "—"}
        </div>
    </div>
);

const SectionHeading = ({ children }) => (
    <div
        style={{
            fontSize: 9,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: "#555",
            borderBottom: "1px solid #ddd",
            paddingBottom: 5,
            marginBottom: 12,
        }}
    >
        {children}
    </div>
);

const Timeline = ({ steps }) => {
    if (!steps.length) return null;
    return (
        <Stepper alternativeLabel style={{ padding: "4px 0", background: "transparent" }}>
            {steps.map((step) => (
                <Step key={step.bigText} completed={step.filled}>
                    <StepLabel>
                        <Typography style={{ fontSize: 8, fontWeight: 700, lineHeight: 1.3 }}>
                            {step.bigText}
                        </Typography>
                        {step.timestamp_utc && (
                            <Typography style={{ fontSize: 7, color: "#aaa", display: "block" }}>
                                {step.smallText}
                                {moment(step.timestamp_utc).format("MM/DD HH:mm")}
                            </Typography>
                        )}
                    </StepLabel>
                </Step>
            ))}
        </Stepper>
    );
};

const StatusCell = ({ label, value, color, mono, colSpan }) => (
    <div style={{ gridColumn: `span ${colSpan}`, padding: "8px 10px", borderRight: "1px solid #e8e8e8" }}>
        <div
            style={{
                fontSize: 8,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.7px",
                color: "#1a1a1a",
                opacity: 0.4,
                marginBottom: 3,
            }}
        >
            {label}
        </div>
        <div
            style={{
                fontSize: 11,
                fontWeight: color ? 700 : 600,
                color: color || "#1a1a1a",
                opacity: 0.85,
                fontFamily: mono ? "monospace" : "inherit",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }}
        >
            {value || "—"}
        </div>
    </div>
);

const OrderReceiptPanel = forwardRef(function OrderReceiptPanel({ order, steps }, ref) {
    const isDelivered = order?.Status >= 8;
    const statusColor = isDelivered ? "#2e7d32" : "#1565c0";

    const fromAddress = [
        order.sSuite ? `Suite ${order.sSuite}` : null,
        order.sAddress,
        order.sQuad,
        [order.sCity, order.sProv].filter(Boolean).join(", "),
        order.sPostal,
        order.sCountry,
    ]
        .filter(Boolean)
        .join(", ");

    const toAddress = [
        order.cSuite ? `Suite ${order.cSuite}` : null,
        order.cAddress,
        order.cQuad,
        [order.cCity, order.cProv].filter(Boolean).join(", "),
        order.cPostal,
        order.cCountry,
    ]
        .filter(Boolean)
        .join(", ");

    return (
        <div
            ref={ref}
            style={{
                fontFamily: "'Arial', 'Helvetica', sans-serif",
                color: "#1a1a1a",
                padding: "28px 32px",
                maxWidth: "740px",
                margin: "0 auto",
                background: "#fff",
            }}
        >
            {/* ── ROW 1: LOGO + TIMELINE ──────────────────────── */}
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 20,
                    marginBottom: 14,
                    paddingBottom: 14,
                    borderBottom: "1.5px solid #ddd",
                }}
            >
                {/* Left: logo + receipt title */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        flexShrink: 0,
                        width: 110,
                    }}
                >
                    {order.brandLogoURL && (
                        <img
                            src={order.brandLogoURL}
                            alt="Logo"
                            style={{
                                height: "auto",
                                width: 90,
                                maxWidth: 90,
                                display: "block",
                                objectFit: "contain",
                                objectPosition: "left",
                                marginBottom: 8,
                            }}
                        />
                    )}
                    <div>
                        <div
                            style={{
                                fontSize: 10,
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.8px",
                                color: "#1a1a1a",
                                lineHeight: 1.2,
                            }}
                        >
                            Delivery Receipt
                        </div>
                        <div style={{ fontSize: 9, fontWeight: 500, color: "#999", marginTop: 2 }}>
                            Order #{order.jobID}
                        </div>
                    </div>
                </div>

                {/* Right: timeline */}
                <div style={{ flex: 1, paddingTop: 2 }}>
                    <Timeline steps={steps} />
                </div>
            </div>

            {/* ── ROW 2: STATUS / TRACKING / SERVICE (12 cols) ── */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(12, 1fr)",
                    marginBottom: 20,
                    background: "#f9f9f9",
                    border: "1px solid #e8e8e8",
                    borderRadius: 4,
                    overflow: "hidden",
                }}
            >
                <StatusCell label="Status" value={getStatusString(order.Status)} color={statusColor} colSpan={3} />
                <StatusCell label="Tracking Number" value={order.TrackNumber} mono colSpan={5} />
                <StatusCell label="Service" value={order.serviceText} colSpan={4} />
            </div>

            {/* ── FROM / TO ──────────────────────────────────── */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 24px 1fr",
                    marginBottom: 20,
                    alignItems: "stretch",
                }}
            >
                <div
                    style={{
                        padding: "14px 16px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px 0 0 4px",
                        background: "#fafafa",
                    }}
                >
                    <div
                        style={{
                            fontSize: 9,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            color: "#888",
                            marginBottom: 6,
                        }}
                    >
                        From
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{order.sCompanyName || "—"}</div>
                    <div style={{ fontSize: 11, color: "#555", lineHeight: 1.5 }}>{fromAddress || "—"}</div>
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#e8f0fe",
                        borderTop: "1px solid #e0e0e0",
                        borderBottom: "1px solid #e0e0e0",
                    }}
                >
                    <span style={{ fontSize: 14, color: "#1565c0", fontWeight: 700 }}>›</span>
                </div>
                <div
                    style={{
                        padding: "14px 16px",
                        border: "1px solid #1565c0",
                        borderRadius: "0 4px 4px 0",
                        background: "#f0f4ff",
                    }}
                >
                    <div
                        style={{
                            fontSize: 9,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            color: "#1565c0",
                            marginBottom: 6,
                        }}
                    >
                        To
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{order.cCompanyName || "—"}</div>
                    <div style={{ fontSize: 11, color: "#444", lineHeight: 1.5 }}>{toAddress || "—"}</div>
                </div>
            </div>

            {/* ── SHIPMENT DETAILS ───────────────────────────── */}
            <div style={{ marginBottom: 20 }}>
                <SectionHeading>Shipment Details</SectionHeading>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px 16px" }}>
                    <Field label="Pieces" value={order.PIECES} />
                    <Field label="Weight" value={order.WEIGHT ? `${order.WEIGHT} lbs` : null} />
                    <Field label="Dimensions" value={order.DimsString || order.dimsString} />
                    <Field label="Description" value={order.DESC} />
                </div>
            </div>

            {/* ── SPECIAL INSTRUCTIONS ───────────────────────── */}
            {order.DEL_INST && (
                <div
                    style={{
                        marginBottom: 20,
                        padding: "10px 14px",
                        background: "#fffbf0",
                        borderLeft: "3px solid #f5a623",
                        borderRadius: "0 4px 4px 0",
                    }}
                >
                    <div
                        style={{
                            fontSize: 9,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            color: "#c47a00",
                            marginBottom: 4,
                        }}
                    >
                        Special Instructions
                    </div>
                    <div style={{ fontSize: 12, color: "#1a1a1a", lineHeight: 1.5 }}>{order.DEL_INST}</div>
                </div>
            )}

            {/* ── DELIVERY DETAILS ───────────────────────────── */}
            <div style={{ marginBottom: 20 }}>
                <SectionHeading>Delivery Details</SectionHeading>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0 16px" }}>
                    <Field label="Signed By" value={order.PROOF_DEL} />
                    <Field
                        label="Delivered"
                        value={
                            order.dtDelivered_display
                                ? `${order.dtDelivered_display} ${order.timezone || ""}`.trim()
                                : null
                        }
                    />
                    <Field label="Delivered To" value={getLocationString(order.location)} />
                    <Field label="Caller" value={order.CALLER} />
                    <Field
                        label="Call In"
                        value={
                            order.dtCallIn_display ? `${order.dtCallIn_display} ${order.timezone || ""}`.trim() : null
                        }
                    />
                    <Field
                        label="Due By"
                        value={order.dtDCT_display ? `${order.dtDCT_display} ${order.timezone || ""}`.trim() : null}
                    />
                    <Field label="Billing Reference" value={order.CUST_REF} />
                    <Field label="Accessories" value={order.Accessories} />
                </div>
            </div>

            {/* ── PROOF OF DELIVERY / SIGNATURE ──────────────── */}
            {(order.signatureURL || isDelivered) && (
                <div style={{ marginBottom: 20 }}>
                    <SectionHeading>Proof of Delivery</SectionHeading>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 20 }}>
                        <div
                            style={{
                                flex: "0 0 auto",
                                border: "1px solid #ddd",
                                borderRadius: 4,
                                padding: "10px 14px",
                                background: "#fafafa",
                                minWidth: 200,
                                textAlign: "center",
                            }}
                        >
                            {order.signatureURL ? (
                                <>
                                    <img
                                        src={order.signatureURL}
                                        alt="Customer Signature"
                                        style={{
                                            display: "block",
                                            maxWidth: "100%",
                                            maxHeight: 60,
                                            width: "auto",
                                            height: "auto",
                                            margin: "0 auto",
                                            objectFit: "contain",
                                        }}
                                    />
                                    <div
                                        style={{
                                            fontSize: 9,
                                            color: "#aaa",
                                            marginTop: 6,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                        }}
                                    >
                                        Signature on file
                                    </div>
                                </>
                            ) : (
                                <div style={{ fontSize: 11, color: "#aaa", padding: "12px 0" }}>
                                    No signature captured
                                </div>
                            )}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px", flex: 1 }}>
                            <Field label="Signed By" value={order.PROOF_DEL} />
                            <Field
                                label="Delivered"
                                value={
                                    order.dtDelivered_display
                                        ? `${order.dtDelivered_display} ${order.timezone || ""}`.trim()
                                        : null
                                }
                            />
                            <Field label="Delivered To" value={getLocationString(order.location)} />
                            <Field label="Status" value={getStatusString(order.Status)} />
                        </div>
                    </div>
                </div>
            )}

            {/* ── FOOTER ─────────────────────────────────────── */}
            <div
                style={{
                    marginTop: 28,
                    paddingTop: 10,
                    borderTop: "1px solid #e0e0e0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <div style={{ fontSize: 9, color: "#aaa", letterSpacing: "0.3px" }}>
                    Automatically generated receipt — Order #{order.jobID}
                </div>
                <div style={{ fontSize: 9, color: "#aaa" }}>
                    {order.dtDelivered_display
                        ? `Delivered: ${order.dtDelivered_display}`
                        : order.dtDCT_display
                          ? `Due: ${order.dtDCT_display}`
                          : ""}
                </div>
            </div>
        </div>
    );
});

export default OrderReceiptPanel;
